import { useEffect, useState } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  ClipboardList,
  Plus,
  Search,
  Trash2,
  Loader2,
  Users,
  BookOpen
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const AdminEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [studentSearchTerm, setStudentSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [enrollmentsRes, coursesRes, usersRes] = await Promise.all([
        axios.get(`${API}/enrollments`),
        axios.get(`${API}/courses`),
        axios.get(`${API}/users`)
      ]);
      setEnrollments(enrollmentsRes.data);
      setCourses(coursesRes.data);
      setUsers(usersRes.data.filter(u => u.role === 'ESTUDIANTE'));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEnroll = async () => {
    if (!selectedCourse || selectedUsers.length === 0) {
      toast.error("Seleccione un curso y al menos un estudiante");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/enrollments/bulk`, {
        course_id: selectedCourse,
        user_ids: selectedUsers
      });
      
      toast.success(`${response.data.created} estudiantes matriculados. ${response.data.already_enrolled} ya estaban matriculados.`);
      setShowEnrollModal(false);
      setSelectedCourse("");
      setSelectedUsers([]);
      setStudentSearchTerm("");
      fetchData();
    } catch (error) {
      toast.error("Error al matricular estudiantes");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEnrollment = async (enrollmentId) => {
    if (!window.confirm("¿Eliminar esta matriculación?")) return;
    
    try {
      await axios.delete(`${API}/enrollments/${enrollmentId}`);
      toast.success("Matriculación eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar matriculación");
    }
  };

  const getUserById = (userId) => users.find(u => u.id === userId) || {};
  const getCourseById = (courseId) => courses.find(c => c.id === courseId) || {};

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completado</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Reprobado</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-800">En Progreso</Badge>;
    }
  };

  const filteredEnrollments = enrollments.filter(enrollment => {
    const user = getUserById(enrollment.user_id);
    const course = getCourseById(enrollment.course_id);
    
    const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCourse = courseFilter === "all" || enrollment.course_id === courseFilter;
    
    return matchesSearch && matchesCourse;
  });

  const studentsNotInCourse = selectedCourse
    ? users.filter(u => !enrollments.some(e => e.course_id === selectedCourse && e.user_id === u.id))
    : users;

  const filteredStudentsNotInCourse = studentsNotInCourse.filter(student => {
    const term = studentSearchTerm.toLowerCase();
    return (
      student.full_name?.toLowerCase().includes(term) ||
      student.username?.toLowerCase().includes(term) ||
      student.cedula?.toLowerCase?.().includes(term) ||
      student.email?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-48 skeleton rounded" />
        <div className="h-96 skeleton rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-enrollments-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-heading">Matrículas</h1>
          <p className="text-slate-600 mt-1">{enrollments.length} matrículas totales</p>
        </div>
        <Button onClick={() => setShowEnrollModal(true)} data-testid="enroll-students-btn">
          <Plus className="w-4 h-4 mr-2" />
          Matricular Estudiantes
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enrollments.length}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enrollments.filter(e => e.status === 'active').length}</p>
                <p className="text-sm text-slate-500">En Progreso</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enrollments.filter(e => e.status === 'completed').length}</p>
                <p className="text-sm text-slate-500">Completados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{enrollments.filter(e => e.status === 'failed').length}</p>
                <p className="text-sm text-slate-500">Reprobados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar por estudiante o curso..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-enrollments-input"
          />
        </div>
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-full sm:w-64" data-testid="course-filter">
            <SelectValue placeholder="Filtrar por curso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los cursos</SelectItem>
            {courses.map((course) => (
              <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Enrollments Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estudiante</TableHead>
              <TableHead>Curso</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Pre-test</TableHead>
              <TableHead>Post-test</TableHead>
              <TableHead>Fecha Matrícula</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEnrollments.map((enrollment) => {
              const user = getUserById(enrollment.user_id);
              const course = getCourseById(enrollment.course_id);
              
              return (
                <TableRow key={enrollment.id} data-testid={`enrollment-row-${enrollment.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{user.full_name || 'Usuario eliminado'}</p>
                      <p className="text-sm text-slate-500">{user.username}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-slate-400" />
                      {course.name || 'Curso eliminado'}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(enrollment.status)}</TableCell>
                  <TableCell>
                    {enrollment.pretest_score !== null 
                      ? `${enrollment.pretest_score.toFixed(0)}%` 
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {enrollment.posttest_score !== null 
                      ? `${enrollment.posttest_score.toFixed(0)}%` 
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(enrollment.enrolled_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteEnrollment(enrollment.id)}
                      className="text-red-600"
                      data-testid={`delete-enrollment-${enrollment.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredEnrollments.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  No se encontraron matriculaciones
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Enroll Modal */}
      <Dialog open={showEnrollModal} onOpenChange={setShowEnrollModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Matricular Estudiantes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Seleccionar Curso *</Label>
              <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                <SelectTrigger data-testid="select-course-enroll">
                  <SelectValue placeholder="Seleccione un curso" />
                </SelectTrigger>
                <SelectContent>
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedCourse && (
              <div className="space-y-2">
                <Label>Seleccionar Estudiantes *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, usuario, cédula o email..."
                    value={studentSearchTerm}
                    onChange={(e) => setStudentSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-students-enroll-input"
                  />
                </div>
                <div className="max-h-64 overflow-auto border rounded-lg p-2 space-y-1">
                  {studentsNotInCourse.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Todos los estudiantes ya están matriculados en este curso
                    </p>
                  ) : filteredStudentsNotInCourse.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No se encontraron estudiantes con ese criterio de búsqueda
                    </p>
                  ) : (
                    filteredStudentsNotInCourse.map((student) => (
                      <div
                        key={student.id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                        onClick={() => {
                          setSelectedUsers(prev =>
                            prev.includes(student.id)
                              ? prev.filter(id => id !== student.id)
                              : [...prev, student.id]
                          );
                        }}
                        data-testid={`student-checkbox-${student.id}`}
                      >
                        <Checkbox checked={selectedUsers.includes(student.id)} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{student.full_name}</p>
                          <p className="text-xs text-slate-500">{student.username}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  {selectedUsers.length} estudiante(s) seleccionado(s)
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEnrollModal(false);
              setSelectedCourse("");
              setSelectedUsers([]);
              setStudentSearchTerm("");
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleBulkEnroll} 
              disabled={saving || !selectedCourse || selectedUsers.length === 0}
              data-testid="confirm-enroll-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Matricular ({selectedUsers.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEnrollments;
